import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommonPage from "@icarius-common/commonPage";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import { getLocalizedString } from "@icarius-localization/strings";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { TextField, Grid, Select, MenuItem, FormControl, InputLabel, Typography, Button } from "@material-ui/core";
import { AddIcon, FileCopyIcon } from "@icarius-icons/index";
import { getAppColor } from "@icarius-app/selectors";
import { VisibilityIcon } from "@icarius-icons/";
import useAutomaticProcesses from "@icarius-common/automaticProcesses/useAutomaticProcesses";
import ResultGridDialog from "@icarius-common/automaticProcesses/components/resultGridDialog";
import PickTemplateDialog from "@icarius-pages/employeesProcesses/components/dialogs/pickTemplateDialog";
import SelectProcessDialog from "@icarius-common/automaticProcesses/components/selectProcessDialog";
import MyPeopleGridDialog from "./dialogs/myPeopleGridDialog";
import NumberItem from "./numberItem";
import useEntryPerConcept from "./useEntryPerConcept";
import CreateEditDialog from "./dialogs/createEditDialog";
import AddConceptStructureDialog from "./dialogs/addConceptStructureDialog";
import DuplicateConceptDialog from "./dialogs/duplicateConceptDialog";
import useFixedFilters from "@icarius-common/fixedFilters";
import paths from "@icarius-router/paths";

const gridStyle = { height: 50, padding: "0px 10px" };

const EntryPerConcept = () => {
    const ownGridRef = useRef();
    const color = useSelector(getAppColor);

    const {
        entryStructures,
        quantityPerUsedEntryStructure,
        dateFormat,
        societyPeople,
        gridData,
        columnDefs,
        fields,
        locale,
        isLoading,
        state,
        handlers,
        colaborators,
        selects,
        structureConcepts,
        concepts,
        conceptsTypes,
        months,
        decimals,
    } = useEntryPerConcept();

    const {
        isExecution,
        automaticProcessesIsLoading,
        automaticProcessesIsEnabled,
        processList,
        resultData,
        resultDialogIsOpen,
        pickTemplateDialogIsOpen,
        processSelectionDialogIsOpen,
        executeProcess,
        handleExecute,
        viewResult,
        handleViewResult,
        handleCloseProcessDialogSelection,
        handleCloseResultDialog,
        handleOpenPickTemplateDialog,
        handleClosePickTemplateDialog,
        handleGenerateDocumentsGivenTemplate,
    } = useAutomaticProcesses(paths.calculateProcessAssetsDiscountsButton);

    const dispatch = useDispatch();

    const goBackButton = () => (
        <CustomIconButton title={getLocalizedString("goBack")} onClick={handlers.handleGoBack} type={"goBack"} />
    );
    const createButton = () => (
        <CustomIconButton title={getLocalizedString("create")} onClick={handlers.handleOpenCreateDialog} type={"add"} />
    );

    const modifyButton = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("edit")}
            onClick={() => handlers.handleOpenEditDialog(gridRef)}
            type={"edit"}
        />
    );

    const duplicateButton = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("replicate")}
            onClick={() => handlers.handleOpenMyPeopleGridDialog(gridRef)}
            type={"duplicate"}
        />
    );

    const structureButton = () => (
        <CustomIconButton
            title={getLocalizedString("exportImportStructure")}
            onClick={handlers.handleExportStructure}
            type={"download"}
        />
    );

    // Procesos automaticos
    const handleValidateAndExecute = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length < 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRow"), severity: "warning" }));
            return;
        }

        const employeeCodes = selectedRows.map((item) => item["Código de empleado"]);
        handleExecute(employeeCodes);
    };

    const executeItem = (gridRef) =>
        automaticProcessesIsEnabled && (
            <CustomIconButton
                title={
                    processList.length === 1
                        ? `${getLocalizedString("calculateProcess")}: ${processList[0].value}`
                        : getLocalizedString("calculateProcess")
                }
                onClick={() => handleValidateAndExecute(gridRef)}
                type={"execute"}
            />
        );

    const handleValidateAndViewResult = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();

        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOnlyOneRow"), severity: "warning" }));
            return;
        }

        handleViewResult(selectedRows[0], selectedRows[0]["Código de empleado"]);
    };

    const viewResultItem = (gridRef) =>
        automaticProcessesIsEnabled && (
            <CustomIconButton
                title={getLocalizedString("viewResult")}
                onClick={() => handleValidateAndViewResult(gridRef)}
            >
                <VisibilityIcon />
            </CustomIconButton>
        );
    // Fin Procesos automaticos

    const deleteItem = (gridRef) => (
        <CustomIconButton
            title={getLocalizedString("delete")}
            onClick={() => handlers.handleOpenDeleteDialog(gridRef)}
            type={"delete"}
        />
    );

    const usedEntryStructures = quantityPerUsedEntryStructure.map((structure) => structure.code);
    const customFixedFilterTitle = `${getLocalizedString("assetsAndDiscountsScreenSelector")} - ${getLocalizedString(
        "byConcept"
    )} - ${state.selectedEntryStructureCode}`;
    const { hasFixedFilters, toggleFixedFilters } = useFixedFilters(customFixedFilterTitle);

    const filtersItem = (gridRef) => (
        <CustomIconButton
            title={hasFixedFilters ? getLocalizedString("removeFixedFilters") : getLocalizedString("setFixedFilters")}
            onClick={() => toggleFixedFilters(gridRef, false)}
            type={"fixedFilter"}
            isSelected={hasFixedFilters}
        />
    );

    return (
        <CommonPage
            title={`${getLocalizedString("assetsAndDiscountsScreenSelector")} - ${getLocalizedString("byConcept")}`}
            isLoading={isLoading || automaticProcessesIsLoading}
            isNotGridPage
            menuItems={state.selectedEntryStructureCode === "" && [goBackButton]}
        >
            <Grid
                container
                item
                alignItems="center"
                justify="space-between"
                xs={12}
                style={{ padding: "10px 24px" }}
                spacing={1}
            >
                <Grid container item alignItems="center" xs={12} md={6} spacing={1}>
                    <Grid container item alignItems="center" style={gridStyle} xs={12} md={6}>
                        <FormControl style={{ width: "100%" }}>
                            <InputLabel>{getLocalizedString("filterEstructura de ingreso")}</InputLabel>
                            <Select
                                value={state.selectedEntryStructureCode}
                                onChange={(e) => handlers.handleChangeEntryStructure(e.target.value)}
                                margin={"none"}
                            >
                                {entryStructures?.map((item) => (
                                    <MenuItem
                                        key={item.code}
                                        disabled={usedEntryStructures.includes(item.code)}
                                        value={item.code}
                                        className={"whiteText"}
                                    >
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid container item alignItems="center" style={gridStyle} xs={12} md={6}>
                        <TextField
                            disabled
                            fullWidth
                            margin={"none"}
                            label={getLocalizedString("periodicity")}
                            value={state.selectedEntryStructurePeriodicity}
                            inputProps={{ maxLength: 8 }}
                        />
                    </Grid>
                </Grid>
                {state.selectedEntryStructureCode !== "" && (
                    <Grid container item alignItems="center" xs={12} md={6} justify="flex-end" spacing={1}>
                        <Grid item>
                            <Button
                                variant="outlined"
                                startIcon={<FileCopyIcon style={{ color }} />}
                                onClick={handlers.handleOpenDuplicatConcept}
                            >
                                {getLocalizedString("duplicateConcept")}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon style={{ color }} />}
                                onClick={handlers.handleOpenAddConceptStructure}
                            >
                                {getLocalizedString("addConcepts")}
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Grid>
            {quantityPerUsedEntryStructure.length > 0 && (
                <Grid container item xs={12} style={{ margin: 10, marginTop: 0 }} spacing={2}>
                    {quantityPerUsedEntryStructure.map((el, index) => (
                        <Grid item key={index}>
                            <NumberItem
                                name={el.name}
                                value={el.cant}
                                selected={el.code === state.selectedEntryStructureCode}
                                onClick={() => handlers.handleChangeEntryStructure(el.code)}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
            {state.selectedEntryStructureCode === "" && (
                <Grid container item xs={12} style={{ margin: "10px 0px" }}>
                    <Grid container alignItems="center" justify="center" direction="column" style={{ marginTop: 150 }}>
                        <Typography
                            color="textSecondary"
                            className="whiteText"
                            variant="h2"
                            style={{ fontSize: 32, fontWeight: 700, marginBottom: 16 }}
                        >
                            {getLocalizedString("selectAnIncomeStructure")}
                        </Typography>
                    </Grid>
                </Grid>
            )}
            {state.myPeopleGridDialogIsOpen && !isLoading && (
                <MyPeopleGridDialog
                    open={state.myPeopleGridDialogIsOpen}
                    data={colaborators}
                    dateFormat={dateFormat}
                    locale={locale}
                    handleConfirm={handlers.handleSetUsers}
                    handleClose={handlers.handleCloseMyPeopleGridDialog}
                />
            )}
            {processSelectionDialogIsOpen && (
                <SelectProcessDialog
                    open={processSelectionDialogIsOpen}
                    title={
                        isExecution
                            ? getLocalizedString("selectProcessToRun")
                            : getLocalizedString("selectProcessToQuery")
                    }
                    processList={processList}
                    handleClose={handleCloseProcessDialogSelection}
                    handleExecute={isExecution ? executeProcess : viewResult}
                />
            )}
            {resultDialogIsOpen && !(isLoading || automaticProcessesIsLoading) && (
                <ResultGridDialog
                    open={resultDialogIsOpen}
                    templates={resultData.templates}
                    process={resultData.process}
                    employeeName={resultData.employee["Apellido y nombres"]}
                    handleClose={handleCloseResultDialog}
                    handleOpenPickTemplateDialog={handleOpenPickTemplateDialog}
                    handleGenerateDocumentsGivenTemplate={handleGenerateDocumentsGivenTemplate}
                />
            )}
            {resultData?.templates?.length > 0 &&
                pickTemplateDialogIsOpen &&
                !(isLoading || automaticProcessesIsLoading) && (
                    <PickTemplateDialog
                        open={pickTemplateDialogIsOpen}
                        templates={resultData.templates}
                        handleConfirm={handleGenerateDocumentsGivenTemplate}
                        handleClose={handleClosePickTemplateDialog}
                    />
                )}
            {state.createEditDialogIsOpen && (
                <CreateEditDialog
                    open={state.createEditDialogIsOpen}
                    loading={isLoading}
                    data={state.selectedDocument}
                    handleClose={handlers.handleCloseCreateEditDialog}
                    concepts={concepts}
                    conceptsTypes={conceptsTypes}
                    selects={selects}
                    fields={fields}
                    months={months}
                    societyPeople={societyPeople}
                    create={handlers.handleCreateEntryPerConcept}
                    modify={handlers.handleModifyEntryPerConcept}
                    decimals={decimals}
                />
            )}
            {state.selectedEntryStructureCode !== "" && customFixedFilterTitle && (
                <CommonPage
                    title={""}
                    menuItems={[
                        goBackButton,
                        createButton,
                        modifyButton,
                        duplicateButton,
                        structureButton,
                        executeItem,
                        viewResultItem,
                        deleteItem,
                        filtersItem,
                    ]}
                    isLoading={isLoading || automaticProcessesIsLoading}
                    gridTitle={getLocalizedString("entryPerConcept")}
                    rowData={gridData}
                    ownColumnDef={columnDefs}
                    dateFormat={dateFormat}
                    hasExpand
                    hasHelp
                    codeFieldName={"internalCode"}
                    ownGridRef={ownGridRef}
                    handleRowClick={handlers.handleRowClick}
                    hasFixedFilters
                    customFixedFilterTitle={customFixedFilterTitle}
                />
            )}
            {state.addConceptStructureIsOpen && (
                <AddConceptStructureDialog
                    open={state.addConceptStructureIsOpen}
                    entryStructure={state.selectedEntryStructureCode}
                    options={structureConcepts.options}
                    selected={structureConcepts.selected}
                    isLoading={isLoading}
                    handleClose={handlers.handleCloseAddConceptStructure}
                />
            )}
            {state.duplicateConceptIsOpen && (
                <DuplicateConceptDialog
                    open={state.duplicateConceptIsOpen}
                    entryStructureCode={state.selectedEntryStructureCode}
                    entryStructureName={
                        entryStructures.find((item) => item.code === state.selectedEntryStructureCode)?.name
                    }
                    selectedConceptsData={structureConcepts.selectedConceptsData}
                    isLoading={isLoading}
                    handleClose={handlers.handleCloseDuplicatConcept}
                />
            )}
        </CommonPage>
    );
};

export default EntryPerConcept;
